import { useParams, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import {
  FaFilePdf,
  FaFileCode,
  FaCube,
  FaExternalLinkAlt,
} from "react-icons/fa";

export default function ProjectDetail() {
  const { id } = useParams();
  const location = useLocation();
  const [project, setProject] = useState(location.state?.proj || null);

  // Fetch if not passed through Link state
  useEffect(() => {
    if (!project) {
      fetch("/data/projects.json")
        .then((res) => res.json())
        .then((data) => {
          const proj = data.find((p) => p.id === id);
          setProject(proj);
        })
        .catch((err) => console.error(err));
    }
  }, [project, id]);

  if (!project) {
    return (
      <div className="text-center py-10">
        <p className="text-gray-600">Project not found.</p>
        <Link
          to="/projects"
          className="text-blue-600 underline hover:text-blue-800"
        >
          ← Back to Projects
        </Link>
      </div>
    );
  }

  // Section definitions
  const sections = [
    { title: "📘 Project Detail", content: project.details },
    { title: "🎯 Objective", content: project.objective },
    { title: "⚙️ Challenges", content: project.challenges, bullet: true },
    { title: "💡 Key Learning", content: project.keyLearning, bullet: true },
    { title: "🧰 Tools & Skills", content: project.toolsSkills, bullet: true },
    {
      title: "🤝 My Responsibilities / Contributions",
      content: project.responsibilities,
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 25 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="max-w-3xl mx-auto px-4 py-10"
    >
      {/* Back Button */}
      <Link
        to="/projects"
        className="flex items-center text-blue-600 hover:text-blue-800 mb-6"
      >
        ← Back to Projects
      </Link>

      {/* Main Image */}
      {project.image && (
        <motion.img
          src={project.image}
          alt={project.title}
          className="w-full h-64 md:h-80 object-cover rounded-2xl shadow-md mb-6"
          initial={{ scale: 0.97 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.6 }}
        />
      )}

      {/* Title */}
      <h2 className="text-3xl font-bold text-gray-900 mb-2">{project.title}</h2>
      {project.date && (
        <p className="text-gray-500 mb-8 text-sm">{project.date}</p>
      )}

      {/* Sections */}
      <div className="space-y-8">
        {sections.map(
          (sec, i) =>
            sec.content &&
            sec.content.trim() && (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
              >
                <h3 className="text-2xl font-semibold text-gray-900 mb-2">
                  {sec.title}
                </h3>

                {/* Render bullets if 'bullet' is true, otherwise paragraph */}
                {sec.bullet ? (
                  <ul className="list-disc pl-5 space-y-1">
                    {sec.content.split("\\n").map((line, idx) => (
                      <li key={idx}>{line}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                    {sec.content}
                  </p>
                )}

                <div className="h-1 w-100 mx-auto my-6 rounded-full bg-gradient-to-r from-blue-300 via-blue-400 to-blue-300"></div>
              </motion.div>
            )
        )}
      </div>
      {/* Resources Section */}
      {project.resources && project.resources.length > 0 && (
        <div className="mt-12">
          <h3 className="text-2xl font-semibold text-gray-900 mb-6">
            📎 Resources & Files
          </h3>
          <div className="flex flex-wrap gap-4">
            {project.resources.map((res, i) => {
              // Choose icon based on type or file extension
              let Icon = FaExternalLinkAlt;
              if (res.type === "file") {
                if (res.url.endsWith(".pdf")) Icon = FaFilePdf;
                else if (
                  res.url.endsWith(".zip") ||
                  res.url.endsWith(".py") ||
                  res.url.endsWith(".ino")
                )
                  Icon = FaFileCode;
                else if (res.url.endsWith(".stl") || res.url.endsWith(".obj"))
                  Icon = FaCube;
              }

              return (
                <a
                  key={i}
                  href={res.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  download={res.type === "file"}
                  className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-blue-500 to-blue-700 text-white font-semibold rounded-lg shadow-lg hover:from-blue-600 hover:to-blue-800 transform hover:-translate-y-1 transition-all duration-200"
                >
                  <Icon className="text-white" />
                  {res.title}
                </a>
              );
            })}
          </div>
          <div className="h-1 w-100 mx-auto my-6 rounded-full bg-gradient-to-r from-blue-300 via-blue-400 to-blue-300"></div>
        </div>
      )}

      {/* Media Gallery Section */}
      {project.media && (
        <div className="mb-12">
          <h3 className="text-2xl font-semibold text-gray-900 mb-6">
            📸 Media Album
          </h3>

          {/* Hero Image */}
          {project.media.hero && (
            <motion.img
              src={project.media.hero}
              alt="Hero"
              className="w-full h-64 md:h-80 object-cover rounded-2xl mb-6 shadow-md"
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            />
          )}

          {/* Grid Gallery */}
          <div className="grid gap-4 md:grid-cols-3">
            {project.media?.gallery?.map((item, i) =>
              item.endsWith(".mp4") ? (
                <motion.video
                  key={i}
                  src={item}
                  controls
                  className="w-full h-48 object-cover rounded-xl shadow-sm"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                />
              ) : (
                <motion.img
                  key={i}
                  src={item}
                  alt={`Media ${i + 1}`}
                  className="w-full h-48 object-cover rounded-xl shadow-sm"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                />
              )
            ) ?? null}
          </div>
        </div>
      )}
    </motion.div>
  );
}
